<template>
  <v-app v-resize="onResize" class="overflow-hidden">
    <Sidebar
      v-if="!editingTemplate && !userIsOnSelfCheckout"
      @executeRequestActivation="requestActivation"
    />

    <!-- NOTIFICATION SIDEBAR -->
    <NotificationsSidebar v-if="currentUser" :color="primaryColor" />

    <!-- NAVBAR -->
    <Navbar
      @snackbarPurchaseInterest="requestActivation"
      @openSupport="openSupport"
      :users="users"
    />

    <v-main :width="bodyWidth" class="background overflow-hidden pb-0">
      <ResizablePage
        styles=""
        bodyStyle=""
        hide-custom-space
        :footer-height="0"
        hide-footer
        :resizable-max-width="false"
      >
        <template v-slot:header>
          <!-- Alerta Modo Reseller -->
          <v-banner
            v-if="isSimulation"
            sticky
            app
            :single-line="!$vuetify.breakpoint.smAndDown"
            :class="`${
              !$vuetify.breakpoint.smAndDown ? 'py-1' : ''
            } background`"
            icon="ph-warning-circle"
            icon-color="warning"
            style="height: auto; top: 0px"
          >
            Modo Parceiro - Você está gerenciando
            <span class="font-weight-bold primary--text">{{ mainDomain }}</span>
            <template v-slot:actions>
              <v-btn
                small
                width="100%"
                outlined
                @click="endSimulationMode"
                class="font-weight-bold"
                rounded
              >
                <v-icon left v-text="'ph-arrow-left'" dense />Voltar
              </v-btn>
            </template>
          </v-banner>
        </template>

        <template v-slot:table="tableProps">
          <v-card
            color="background"
            flat
            tile
            class="overflow-y-auto ma-0 pa-0 rounded-0"
            :height="tableProps.tableHeight"
          >
            <v-container
              fluid
              class="ma-0 pa-0 mt-5 background"
              v-if="missingScopesSuite.length && !isDashTester"
            >
              <v-card
                class="mx-auto elevation-0 px-2 pb-3 mb-4 background"
                flat
              >
                <ScopeAlert class="ma-3 mb-0" />
              </v-card>
            </v-container>
            <router-view />
          </v-card>
        </template>
      </ResizablePage>
    </v-main>

    <!-- LOCAL SNACKBAR -->
    <CsSnackbar
      :show="showSnackbar"
      :message="snackbarText"
      :color="snackbarType"
      @close="showSnackbar = false"
    />

    <!-- GLOBAL SNACKBAR -->
    <CsSnackbar
      data-testid="snackbar-global"
      :show="isShowingSnackbar"
      :snackbar-messages="snackbarMessages"
      :message-key="snackbarHasMessageKey ? snackbarMessageKey : ''"
      :message="snackbarMessage"
      :timeout="snackbarTimeout"
      :notShowCloseButton="snackbarShowCloseButton"
      :color="snackbarColor"
      @close="resetSnackbarInfo"
      style="z-index: 999"
    />

    <ActionsScheduleMeeting
      v-if="showScheduleMeeting && !isSimulation && !isAdminPage"
    />

    <PriceChangeNotice />

    <v-btn
      v-show="chatLoading"
      color="primary"
      elevation="2"
      fab
      loading
      class="fab-button-chat"
    ></v-btn>

    <SimpleDialog
      :show="isVisibleModalSupport"
      :title="$t('supportDialog.title')"
      :showCancel="false"
      :readOnly="true"
      @close="closeSupportModal"
      width="500"
    >
      <template v-slot:body>
        <p
          class="mt-2 text-title-1"
          v-html="$t('supportDialog.message', { name: resellerCompanyName })"
        ></p>
      </template>
    </SimpleDialog>
  </v-app>
</template>

<script>
import ActionsScheduleMeeting from "@/components/base/ActionsScheduleMeeting.vue";
import PriceChangeNotice from "@/components/base/PriceChangeNotice.vue";
import ResizablePage from "@/components/base/ResizablePage.vue";
import ScopeAlert from "@/components/home/ScopeAlert.vue";
import Navbar from "@/components/main/dashboard/Navbar.vue";
import Sidebar from "@/components/main/dashboard/Sidebar.vue";
import NotificationsSidebar from "@/components/main/notifications/NotificationsSidebar.vue";
import {
  setPathDefaultHubSpotTrack,
  setTrackingPathsHubspot,
} from "@/helpers/hubspot";
import { dateIsToday } from "@/helpers/services/utils";
import { snackbarMessages } from "@/helpers/variables/snackbarMessages";
import { getAnalytics, logEvent } from "firebase/analytics";
import { mapActions, mapGetters, mapMutations } from "vuex";

export default {
  components: {
    NotificationsSidebar,
    Sidebar,
    Navbar,
    ResizablePage,
    ActionsScheduleMeeting,
    PriceChangeNotice,
    ScopeAlert,
  },
  data: () => ({
    showSnackbar: false,
    snackbarType: "success",
    snackbarText: "",
    showBlackFriday: false,
    bodyWidth: 0,

    snackbarMessages,
  }),

  computed: {
    ...mapGetters([
      "currentRoute",
      "hasBillingPermission",
      "primaryColor",
      "company",
      "showScheduleMeeting",
      "users",
      "currentUser",
      "isSimulation",
      "isConecta",
      "token",
      "snackbar",
      "isShowingSnackbar",
      "snackbarTimeout",
      "snackbarHasMessageKey",
      "snackbarMessage",
      "snackbarColor",
      "snackbarShowCloseButton",
      "snackbarMessageKey",
      "feedbackButtonClick",
      "isSmallClient",
      "hasProcessRunning",
      "emailTransferInProgressPath",
      "chatLoading",
      "missingScopesSuite",
      "isDashTester",
      "isVisibleModalSupport",
      "resellerCompanyName",
      "mainDomain",
    ]),

    isAdminPage() {
      return [
        "ResellerCustomers",
        "ResellerClientInfo",
        "ResellerNewPlan",
        "ResellerNewService",
        "ResellerBilling",
        "CentralNotifications",
        "BanClients",
        "DemoLoginConfig",
        "ServicesManager",
        "PlansManager",
      ].includes(this.$route.name);
    },

    userIsOnSelfCheckout() {
      return (
        this.currentRoute === "BillingUpgradePlan" && this.hasBillingPermission
      );
    },

    editingTemplate() {
      return this.$route.name.includes("TemplateEdit");
    },

    feedbacksButtons() {
      return document.getElementsByClassName("feedback-button");
    },

    hasMigrationRunning() {
      return this.hasProcessRunning || this.emailTransferInProgressPath;
    },

    isCompanyCreatedToday() {
      return dateIsToday(this.company.created_at);
    },
  },

  watch: {
    showBlackFriday() {
      if (!this.showBlackFriday) {
        this.showBlackFriday = false;
        localStorage.setItem("showBlackFriday", "false");
      }
    },

    "$route.path": async function () {
      if (
        !["ResellerNewPlan", "ResellerClientInfo"].includes(this.$route.name)
      ) {
        localStorage.removeItem("selectedResellerClient", "");
      }
      if (!this.isSimulation) {
        setTrackingPathsHubspot(this.$route.fullPath);
      }
    },

    feedbacksButtons(newValue, oldValue) {
      if (newValue.lenght !== oldValue.lenght) {
        this.removeFeedback();
        this.setFeedback();
      }
    },

    feedbackButtonClick() {
      this.showCollectorDialog();
    },
    hasMigrationRunning(value) {
      if (value) {
        window.addEventListener("beforeunload", this.tasksProcessesHandler);
        window.addEventListener("beforeunload", this.leaveDriveProcessRoom);
        window.addEventListener("unload", this.leaveDriveProcessRoom);
      } else {
        window.removeEventListener("beforeunload", this.tasksProcessesHandler);
        window.removeEventListener("beforeunload", this.leaveDriveProcessRoom);
        window.removeEventListener("unload", this.leaveDriveProcessRoom);
      }
    },
  },

  methods: {
    ...mapMutations([
      "setSidebarView",
      "setLoading",
      "resetSnackbarInfo",
      "setLocale",
      "setOpenModalSupport",
    ]),

    tasksProcessesHandler(event) {
      event.preventDefault();
      event.returnValue = "";
    },

    ...mapActions([
      "getUsers",
      "purchaseInterest",
      "getScopes",
      "getPlans",
      "syncUos",
      "getUos",
      "leaveDriveProcessRoom",
      "getSyncUsersStatus",
      "endSimulationMode",
    ]),

    openSupport() {
      if (this.$route.name != "ContactUs")
        this.$router.push({ name: "ContactUs" });
    },

    loadJiraFeedbackIntegration() {
      var s = document.createElement("script");
      s.src =
        "https://conectanuvem.atlassian.net/s/d41d8cd98f00b204e9800998ecf8427e-T/1jmxwi/b/8/c95134bc67d3a521bb3f4331beb9b804/_/download/batch/com.atlassian.jira.collector.plugin.jira-issue-collector-plugin:issuecollector/com.atlassian.jira.collector.plugin.jira-issue-collector-plugin:issuecollector.js?locale=pt-BR&collectorId=12a150ca";
      document.body.appendChild(s);
    },

    setFeedback() {
      const _this = this;
      window.ATL_JQ_PAGE_PROPS = {
        triggerFunction: function (showCollectorDialog) {
          _this.feedbacksButtons.forEach((element) => {
            element.addEventListener("click", (e) => {
              e.preventDefault();
              showCollectorDialog();
            });
          });
          _this.showCollectorDialog = showCollectorDialog;
        },
      };
    },

    showCollectorDialog() {},

    removeFeedback() {
      const _this = this;
      window.ATL_JQ_PAGE_PROPS = {
        triggerFunction: function () {
          _this.feedbacksButtons.forEach((element) => {
            element.removeEventListener("click", false);
          });
        },
      };
    },

    onResize() {
      this.bodyWidth = this.sidebarMini
        ? window.innerWidth - 230
        : window.innerWidth - 300;
    },

    updateContract() {
      const url = `${process.env.VUE_APP_API_BASE_URL}/company/contract`;
      const auth = {
        headers: {
          Authorization: this.token,
        },
      };
      this.$axios.put(url, {}, auth);
    },

    requestActivation(product) {
      const analytics = getAnalytics();

      logEvent(analytics, "go_to_self_checkout", {
        click: "trial_bar",
        main_domain: this.mainDomain,
        email: this.currentUser.email,
        description:
          "Clicou no botão 'Contratar plano' do menu de trial na barra superior (navbar)",
      });

      this.$router.push("/billing/add-plan");

      // Envio de evento RD informando sobre interesse de aquisição de plano
      this.purchaseInterest(product)
        .then(() => {
          // this.textSuccess = "Solicitação realizada com sucesso!";
          // this.snackbarSuccess = true;
        })
        .catch(() => {
          this.textFailure = "Erro ao realizar a solicitação!";
          this.snackbarFailure = true;
        });
    },

    saveLocale(lang) {
      this.$i18n.locale = lang;
      this.$vuetify.lang.current = lang == "pt-br" ? "pt" : lang;
      localStorage.setItem("lang", lang);
      this.setLocale(lang);
    },

    closeSupportModal() {
      this.setOpenModalSupport(false);
    },
  },

  async beforeMount() {
    this.setLoading(true);
    this.saveLocale(this.company?.language || "pt-br");

    if (this.$vuetify.breakpoint.mobile) {
      this.setSidebarView(false);
    }
    this.getScopes();
    if (!this.company.uos) {
      this.syncUos();
    }
    if (this.isCompanyCreatedToday) this.getSyncUsersStatus();
    await Promise.all([
      this.loadJiraFeedbackIntegration(),
      this.getUsers(),
      this.getPlans(),
      this.getUos(),
    ]).then(() => {
      if (
        !this.isSimulation &&
        !this.company.contract &&
        this.currentUser.is_google_admin
      ) {
        this.updateContract();
      }
      this.setLoading(false);
    });

    if (this.isSimulation) {
      return;
    }

    setPathDefaultHubSpotTrack();
  },

  mounted() {
    this.setFeedback();
  },

  beforeDestroy() {
    this.removeFeedback();
    if (!this.isConecta) {
      window.removeEventListener("beforeunload", this.leaveDriveProcessRoom);
      window.removeEventListener("unload", this.leaveDriveProcessRoom);
    }
    window.removeEventListener("beforeunload", this.tasksProcessesHandler);
  },
};
</script>
